// ─── Blueprint equipment drawings (home screen) ──────────────────────────────
// Filled technical drawings on a blue grid panel (BlueprintPanel). Trucks are
// light shapes with darker panel lines and dark glass / tyres / gaps, so they
// stand out from the sheet. Every drawing shares the same truck (cab, chassis,
// wheels) in a 120×80 frame; only the body on the back changes.

export const BP_BG = "#2E5F99";
const BODY = "#C7D9EE";     // main bodywork fill
const BODY2 = "#9DBADF";    // secondary parts: chassis gear, rams, frames, stands
const CAB = "#E6EEF8";      // cab + highlights
const DARK = "#1C3B63";     // tyres, glass, gaps, pins
const GLASS = "#2A5585";    // windows
const EDGE = "#15325A";     // outline of every part
const DETAIL = "#5578A6";   // panel lines drawn on parts
const FREE = "#DCE8F6";     // lines drawn straight on the sheet (ground, dimensions, rails)
const GRID = "255,255,255"; // grid + panel edge colour (r,g,b)
const SHADOW = "drop-shadow(0 2px 3px rgba(5,20,45,0.35))";

// Solid part drawn as an outline (dark edge, filled) — booms, rams, frames
const Beam = ({ x1, y1, x2, y2, w = 4.4, fill = BODY, rod }) => (
  <g strokeLinecap="round">
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={EDGE} strokeWidth={w} />
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={fill} strokeWidth={w - 1.5} />
    {rod && <line x1={x1} y1={y1} x2={(x1 + x2) / 2} y2={(y1 + y2) / 2} stroke={CAB} strokeWidth={0.7} />}
  </g>
);
const Pin = ({ cx, cy, r = 1.7 }) => (
  <g><circle cx={cx} cy={cy} r={r} fill={DARK} /><circle cx={cx} cy={cy} r={0.55} fill={FREE} stroke="none" /></g>
);
const Wheel = ({ cx }) => (
  <g>
    <circle cx={cx} cy={64} r={8} fill={DARK} />
    <circle cx={cx} cy={64} r={4.9} fill={BODY2} />
    <circle cx={cx} cy={64} r={1.9} fill={CAB} />
    {[0, 72, 144, 216, 288].map(a => {
      const t = (a - 90) * Math.PI / 180;
      return <circle key={a} cx={cx + Math.cos(t) * 3.3} cy={64 + Math.sin(t) * 3.3} r={0.45} fill={DARK} stroke="none" />;
    })}
  </g>
);
const Outrigger = ({ x }) => (
  <g>
    <rect x={x - 1} y={58} width={2} height={12} fill={BODY2} />
    <rect x={x - 3.2} y={70} width={6.4} height={1.8} fill={DARK} />
  </g>
);
// Row of toolbox / locker doors with handles and a dark kick strip
const Lockers = ({ x, y, w, h, n }) => {
  const dw = w / n;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill={BODY} />
      {Array.from({ length: n }, (_, i) => (
        <g key={i}>
          <rect x={x + i * dw + 1} y={y + 1} width={dw - 2} height={h - 3} rx={0.4} stroke={DETAIL} strokeWidth={0.5} />
          <line x1={x + i * dw + dw / 2 - 2} y1={y + 3} x2={x + i * dw + dw / 2 + 2} y2={y + 3} strokeWidth={0.9} />
        </g>
      ))}
      <rect x={x} y={y + h - 1.6} width={w} height={1.6} fill={DARK} stroke="none" />
    </g>
  );
};
// Hose reel seen side-on: flange, dark gaps between spokes, drum + hub, on a stand
const Reel = ({ cx, cy, r, standTo }) => (
  <g>
    {standTo != null && (
      <g>
        <line x1={cx} y1={cy} x2={cx - r * 0.75} y2={standTo} stroke={EDGE} strokeWidth={2.2} />
        <line x1={cx} y1={cy} x2={cx - r * 0.75} y2={standTo} stroke={BODY2} strokeWidth={1.1} />
        <line x1={cx} y1={cy} x2={cx + r * 0.75} y2={standTo} stroke={EDGE} strokeWidth={2.2} />
        <line x1={cx} y1={cy} x2={cx + r * 0.75} y2={standTo} stroke={BODY2} strokeWidth={1.1} />
      </g>
    )}
    <circle cx={cx} cy={cy} r={r} fill={BODY2} />
    <circle cx={cx} cy={cy} r={r * 0.8} fill={DARK} stroke="none" />
    {[0, 60, 120, 180, 240, 300].map(a => {
      const t = (a * Math.PI) / 180;
      return <line key={a} x1={cx} y1={cy} x2={cx + Math.cos(t) * r * 0.8} y2={cy + Math.sin(t) * r * 0.8} stroke={BODY2} strokeWidth={Math.max(0.8, r * 0.14)} />;
    })}
    <circle cx={cx} cy={cy} r={r * 0.42} fill={CAB} />
    <circle cx={cx} cy={cy} r={r * 0.42 - 0.9} stroke={DETAIL} strokeWidth={0.4} strokeDasharray="0.9 0.7" />
    <circle cx={cx} cy={cy} r={Math.max(0.8, r * 0.13)} fill={DARK} />
  </g>
);
// Hose: dark-edged tube
const Hose = ({ d, w = 2.4 }) => (
  <g fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} stroke={EDGE} strokeWidth={w} />
    <path d={d} stroke={BODY2} strokeWidth={w - 1.3} />
  </g>
);

// Truck base: ground, chassis, fuel tank, cab, wheels, overall-length dimension.
const Truck = ({ size, children }) => (
  <svg viewBox="0 0 120 80" width="100%" fill="none" stroke={EDGE}
    strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block", overflow: "visible", maxWidth: size, aspectRatio: "3 / 2", filter: SHADOW }}>
    {/* ground + overall length dimension */}
    <g stroke={FREE} strokeWidth="0.4" opacity="0.5">
      <line x1="1" y1="72.4" x2="119" y2="72.4" strokeDasharray="2 1.6" />
      <line x1="4" y1="77" x2="116" y2="77" />
      <line x1="4" y1="75" x2="4" y2="79" /><line x1="116" y1="75" x2="116" y2="79" />
      <path d="M4 77 l3 -1.1 v2.2 Z M116 77 l-3 -1.1 v2.2 Z" fill={FREE} />
    </g>
    {/* chassis rail, fuel tank, rear lights + mud flap */}
    <rect x="27" y="55" width="88" height="4" fill={DARK} />
    <rect x="43" y="58.5" width="12" height="6" rx="2.4" fill={BODY2} />
    <line x1="46.5" y1="58.5" x2="46.5" y2="64.5" stroke={DETAIL} strokeWidth="0.5" /><line x1="51.5" y1="58.5" x2="51.5" y2="64.5" stroke={DETAIL} strokeWidth="0.5" />
    <rect x="111" y="59" width="1.3" height="8" fill={DARK} />
    <rect x="113" y="56" width="2.4" height="1.8" fill={CAB} />
    {children}
    {/* cab with wheel arch */}
    <path d="M5 60 V38 Q5 31 12 31 H27 V60 H24.9 A9.3 9.3 0 0 0 7.1 60 Z" fill={CAB} />
    <rect x="11" y="29.6" width="8" height="1.6" rx="0.6" fill={BODY2} />
    {/* window with reflection */}
    <path d="M8 33.8 H24.5 V43.5 H8 Z" fill={GLASS} />
    <line x1="11" y1="43" x2="16" y2="34.3" stroke={FREE} strokeWidth="1" opacity="0.35" />
    <line x1="14" y1="43" x2="18.2" y2="35.8" stroke={FREE} strokeWidth="0.5" opacity="0.3" />
    {/* door, handle, step */}
    <path d="M10 33 V52 Q10 54 12 54 H25.5" stroke={DETAIL} strokeWidth="0.5" />
    <line x1="21" y1="46.5" x2="23.5" y2="46.5" strokeWidth="0.9" />
    <rect x="17" y="57.2" width="7" height="1.6" fill={BODY2} />
    {/* mirror, headlight, bumper, grille */}
    <line x1="5" y1="36.5" x2="2.6" y2="35.4" strokeWidth="0.6" />
    <rect x="1.4" y="33.5" width="1.8" height="5.6" rx="0.5" fill={DARK} />
    <rect x="5" y="49.5" width="2" height="3" rx="0.5" fill={FREE} />
    <line x1="5.6" y1="44.5" x2="5.6" y2="48" stroke={DETAIL} strokeWidth="0.5" />
    <rect x="2.5" y="55" width="5.5" height="5.5" rx="0.6" fill={BODY2} />
    <line x1="2.5" y1="57.6" x2="8" y2="57.6" stroke={DETAIL} strokeWidth="0.5" />
    <Wheel cx={16} /><Wheel cx={84} /><Wheel cx={102} />
  </svg>
);

// Rounded tank used by the vacuum-family drawings: bands, highlight, saddles
const Tank = ({ x = 32, y = 24, w = 80, h = 28, bands = [52, 72, 92] }) => (
  <g>
    <rect x={x - 2} y={y + h} width={w + 2} height={3} fill={DARK} />
    {[x + w * 0.25, x + w * 0.7].map(sx => <rect key={sx} x={sx - 2.5} y={y + h - 3} width={5} height={4} fill={BODY2} />)}
    <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={BODY} />
    {bands.map(b => <rect key={b} x={b - 0.8} y={y + 0.3} width={1.6} height={h - 0.6} fill={BODY2} stroke={DETAIL} strokeWidth={0.4} />)}
    <line x1={x + h / 2} y1={y + 3.6} x2={x + w - h / 2} y2={y + 3.6} stroke={CAB} strokeWidth="1.4" opacity="0.9" />
    <line x1={x + h / 2} y1={y + h - 4} x2={x + w - h / 2} y2={y + h - 4} stroke={DETAIL} strokeWidth="0.5" />
  </g>
);
// Rear-mounted suction boom on top of a tank: turret at (px, py), arm up and back, hose down
const RearBoom = ({ px, py, ex, ey, hoseTo }) => (
  <g>
    <rect x={px - 4.5} y={py - 1} width={9} height={5.5} rx={1} fill={BODY2} />
    <rect x={px - 5.5} y={py + 4} width={11} height={1.4} fill={DARK} />
    <Beam x1={px - 2.5} y1={py + 2.5} x2={(px + ex) / 2 - 2} y2={(py + ey) / 2 + 2} w={2.8} fill={BODY2} rod />
    <Beam x1={px} y1={py} x2={ex} y2={ey} w={4.4} />
    <Pin cx={px} cy={py} />
    <Hose d={`M${ex} ${ey} Q${ex + 5} ${ey} ${ex + 5} ${ey + 5} V${hoseTo}`} />
    <rect x={ex + 3.4} y={hoseTo} width={3.2} height={2.6} rx={0.5} fill={DARK} />
  </g>
);
// Small cyclone / separator ahead of the manway on vacuum tanks
const Cyclone = ({ x }) => (
  <g>
    <rect x={x} y={13} width={7} height={11} rx={1} fill={BODY2} />
    <line x1={x} y1={17} x2={x + 7} y2={17} stroke={DETAIL} strokeWidth="0.5" />
    <rect x={x + 2.5} y={9} width={2} height={4} fill={DARK} />
  </g>
);
const Manway = ({ x }) => (
  <g>
    <rect x={x} y={19} width={12} height={5} rx={1} fill={BODY2} />
    <rect x={x + 1.5} y={17.6} width={9} height={1.6} rx={0.5} fill={CAB} />
  </g>
);

export const EwpIcon = ({ size = 120 }) => (
  <Truck size={size}>
    {/* toolbox body with top handrail and rear ladder */}
    <Lockers x={30} y={41} w={82} h={14} n={6} />
    <g stroke={FREE} strokeWidth="0.6">
      <line x1="44" y1="41" x2="44" y2="37.5" /><line x1="58" y1="41" x2="58" y2="37.5" /><line x1="72" y1="41" x2="72" y2="37.5" />
      <line x1="44" y1="37.5" x2="72" y2="37.5" />
      <line x1="112.6" y1="41" x2="114.6" y2="57" /><line x1="115.4" y1="41" x2="117.4" y2="57" />
      {[45, 49, 53].map(y => <line key={y} x1={112.6 + (y - 41) / 8} y1={y} x2={115.4 + (y - 41) / 8} y2={y} />)}
    </g>
    <Outrigger x={33} /><Outrigger x={109} />
    {/* boom rest */}
    <rect x="38.8" y="34" width="2.4" height="7" fill={BODY2} /><rect x="36.5" y="32.6" width="7" height="1.6" rx="0.6" fill={DARK} />
    {/* turret on slew ring */}
    <rect x="86.5" y="39.6" width="18" height="1.6" fill={DARK} />
    <path d="M88 39.6 V35 Q88 32 91 32 H100 Q103 32 103 35 V39.6 Z" fill={BODY2} />
    <line x1="90" y1="36.5" x2="101" y2="36.5" stroke={DETAIL} strokeWidth="0.5" />
    {/* lift ram, lower + upper boom (with insulated section) */}
    <Beam x1={98} y1={37} x2={83} y2={25} w={3} fill={BODY2} rod />
    <Beam x1={95} y1={35} x2={62} y2={15} w={4.8} />
    <Beam x1={62} y1={15} x2={100} y2={6.5} w={4.6} />
    <Beam x1={86} y1={9.2} x2={95} y2={7.2} w={4.6} fill={CAB} />
    <line x1="64" y1="14.5" x2="84" y2="10" stroke={DETAIL} strokeWidth="0.5" />
    <Pin cx={95} cy={35} /><Pin cx={62} cy={15} /><Pin cx={100} cy={6.5} r={1.4} />
    {/* bucket with lip and control panel */}
    <rect x="101" y="3" width="12" height="11.5" rx="1" fill={CAB} />
    <rect x="100.5" y="2.4" width="13" height="2" rx="0.6" fill={BODY2} />
    <rect x="103" y="6.5" width="4" height="3" rx="0.4" fill={DARK} />
    <line x1="101" y1="12" x2="113" y2="12" stroke={DETAIL} strokeWidth="0.5" />
    {/* working height dimension */}
    <g stroke={FREE} strokeWidth="0.4" opacity="0.5">
      <line x1="119" y1="2.4" x2="119" y2="72.4" />
      <path d="M119 2.4 l-1.1 3 h2.2 Z M119 72.4 l-1.1 -3 h2.2 Z" fill={FREE} />
    </g>
  </Truck>
);

export const CraneIcon = ({ size = 120 }) => (
  <Truck size={size}>
    {/* deck with dropsides */}
    <rect x="30" y="47" width="85" height="8" fill={BODY} />
    <rect x="30" y="47" width="85" height="1.5" fill={BODY2} />
    {[50, 70, 90].map(x => <line key={x} x1={x} y1={48.5} x2={x} y2={54.5} stroke={DETAIL} strokeWidth="0.5" />)}
    {[40, 60, 80, 100].map(x => <rect key={x} x={x} y={52} width={2} height={1.4} fill={DARK} stroke="none" />)}
    {/* headboard / pole rack behind the cab */}
    <rect x="29" y="18" width="6.4" height="29" fill={DARK} />
    {[18, 25, 32, 39].map(y => <rect key={y} x="29" y={y} width="6.4" height="1.6" fill={BODY2} />)}
    <rect x="29" y="18" width="1.6" height="29" fill={BODY2} /><rect x="33.8" y="18" width="1.6" height="29" fill={BODY2} />
    <Beam x1={35.5} y1={20} x2={42} y2={20} w={2.4} fill={BODY2} />
    <Beam x1={42} y1={20} x2={42} y2={23} w={2.4} fill={BODY2} />
    <Outrigger x={111} />
    {/* crane on the rear: base, column, controls */}
    <rect x="97.5" y="45.6" width="13" height="1.6" fill={DARK} />
    <path d="M99 45.6 V35 Q99 31.5 102.5 31.5 H105.5 Q109 31.5 109 35 V45.6 Z" fill={BODY2} />
    <line x1="100.5" y1="40" x2="107.5" y2="40" stroke={DETAIL} strokeWidth="0.5" />
    <g stroke={FREE} strokeWidth="0.6"><line x1="110" y1="42" x2="112" y2="38.5" /><line x1="111.5" y1="42" x2="113.5" y2="38.5" /></g>
    {/* ram, main boom, knuckle boom + extension */}
    <Beam x1={103} y1={43} x2={94} y2={22} w={3.2} fill={BODY2} rod />
    <Beam x1={104} y1={34} x2={84} y2={10} w={5.2} />
    <Beam x1={84} y1={10} x2={62} y2={16} w={4.6} />
    <Beam x1={62} y1={16} x2={50} y2={19.3} w={3.2} fill={CAB} />
    <line x1="101" y1="30" x2="87" y2="13.5" stroke={DETAIL} strokeWidth="0.5" />
    <Pin cx={104} cy={34} /><Pin cx={84} cy={10} />
    {/* hoist line, hook block + hook */}
    <line x1="50" y1="19.3" x2="50" y2="29" stroke={FREE} strokeWidth="0.6" />
    <rect x="48" y="29" width="4" height="3.2" rx="0.6" fill={BODY2} />
    <path d="M50 32.2 v1.6 a2.6 2.6 0 1 1 -2.6 2.6" stroke={FREE} strokeWidth="1.1" />
    <Pin cx={50} cy={19.3} r={1.3} />
  </Truck>
);

export const VacuumIcon = ({ size = 120 }) => (
  <Truck size={size}>
    {/* underbody locker */}
    <rect x="58" y="58.5" width="15" height="6.5" rx="0.6" fill={BODY} />
    <line x1="63.5" y1="61" x2="67.5" y2="61" strokeWidth="0.9" />
    <Tank w={74} bands={[50, 68, 86]} />
    <Manway x={56} />
    <Cyclone x={37} />
    {/* rear door: seal ring, hinge, locking dogs */}
    <circle cx="97" cy="38" r="8.5" fill={CAB} />
    <circle cx="97" cy="38" r="6.8" stroke={DETAIL} strokeWidth="0.5" />
    <rect x="95" y="28.2" width="4" height="2" fill={BODY2} />
    {[45, 135, 225, 315].map(a => { const t = a * Math.PI / 180; return <rect key={a} x={97 + Math.cos(t) * 8.5 - 0.9} y={38 + Math.sin(t) * 8.5 - 0.9} width={1.8} height={1.8} fill={DARK} stroke="none" />; })}
    <RearBoom px={97} py={20} ex={110} ey={7} hoseTo={40} />
  </Truck>
);

export const CombiIcon = ({ size = 120 }) => (
  <Truck size={size}>
    <rect x="58" y="58.5" width="15" height="6.5" rx="0.6" fill={BODY} />
    <line x1="63.5" y1="61" x2="67.5" y2="61" strokeWidth="0.9" />
    <Tank w={66} bands={[48, 64, 80]} />
    <Manway x={54} />
    <Cyclone x={37} />
    {/* jetting reel sticking out past the rear of the tank, on brackets */}
    <Beam x1={95} y1={44} x2={105} y2={40} w={2.4} fill={BODY2} />
    <Beam x1={95} y1={32} x2={105} y2={36} w={2.4} fill={BODY2} />
    <Reel cx={106} cy={38} r={8.5} />
    <RearBoom px={89} py={20} ex={104} ey={7} hoseTo={24} />
  </Truck>
);

export const JettaIcon = ({ size = 120 }) => (
  <Truck size={size}>
    {/* flat deck, water tank */}
    <rect x="30" y="51" width="85" height="4" fill={BODY2} />
    <Tank x={42} y={27} w={52} h={24} bands={[60, 76]} />
    <rect x="62" y="23.6" width="8" height="3.6" rx="0.8" fill={BODY2} />
    {/* small hand reel up front, main jetting reel off the back */}
    <Reel cx={35} cy={42} r={5.4} standTo={51} />
    <Reel cx={105} cy={34.5} r={10.5} standTo={51} />
    <Hose d="M105 45 Q105 48.5 109 48.5 H115" w={2.2} />
  </Truck>
);

export const TrafficTruckIcon = ({ size = 120 }) => (
  <Truck size={size}>
    {/* deck with dropsides */}
    <rect x="30" y="47" width="72" height="8" fill={BODY} />
    <rect x="30" y="47" width="72" height="1.5" fill={BODY2} />
    {[48, 66, 84].map(x => <line key={x} x1={x} y1={48.5} x2={x} y2={54.5} stroke={DETAIL} strokeWidth="0.5" />)}
    {/* goalpost frame behind cab + overhead harness rail with anchor points */}
    <Beam x1={31} y1={47} x2={31} y2={12} w={3.2} fill={BODY2} />
    <Beam x1={31} y1={12} x2={95} y2={12} w={3.2} fill={BODY2} />
    <Beam x1={31} y1={21} x2={40} y2={12} w={2} fill={BODY2} />
    {[48, 62, 76].map(x => <g key={x}><rect x={x - 1.6} y={13.4} width={3.2} height={1.4} fill={DARK} /><circle cx={x} cy={16.6} r={1.2} stroke={FREE} strokeWidth="0.6" /></g>)}
    {/* rear sign board: chevrons + dark arrow display */}
    <rect x="94" y="9" width="14" height="38" rx="1" fill={CAB} />
    <g stroke="#88A7CF" strokeWidth="2" strokeLinecap="butt">
      {[17, 24.5, 32, 39.5, 47].map(y => <polyline key={y} points={`94.8,${y} 101,${y - 6.2} 107.2,${y}`} />)}
    </g>
    <rect x="94" y="9" width="14" height="38" rx="1" />
    <rect x="96" y="21.5" width="10" height="10" rx="0.6" fill={DARK} />
    <g fill={FREE} stroke="none">
      {[[98, 26.5], [99.6, 26.5], [101.2, 26.5], [102.8, 26.5], [101.6, 24.4], [102.6, 25.4], [101.6, 28.6], [102.6, 27.6], [104.2, 26.5]].map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={0.5} />)}
    </g>
    {/* attenuator packed up on the back: hinge, struts, striped face */}
    <Beam x1={108} y1={52} x2={110.5} y2={52} w={2.2} fill={BODY2} />
    <rect x="110" y="19" width="8.5" height="37" rx="1" fill={BODY} />
    <g stroke={DETAIL} strokeWidth="1.4" strokeLinecap="butt">
      {[27, 33, 39, 45, 51].map(y => <line key={y} x1="110.5" y1={y} x2="118" y2={y - 4.5} />)}
    </g>
    <rect x="110" y="19" width="8.5" height="37" rx="1" />
    <line x1="110" y1="37.5" x2="118.5" y2="37.5" strokeWidth="0.7" />
    <Pin cx={111.5} cy={54} r={1.2} />
    {/* beacon on cab roof */}
    <rect x="11" y="27.6" width="8" height="2.4" rx="1" fill={FREE} />
  </Truck>
);

// Blue grid "drawing sheet" the drawings sit on
export const BlueprintPanel = ({ children, active }) => (
  <div style={{
    width: "100%", borderRadius: 8, padding: "14px 12px 8px", boxSizing: "border-box",
    display: "flex", justifyContent: "center",
    backgroundColor: BP_BG,
    backgroundImage: `linear-gradient(rgba(${GRID},0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(${GRID},0.09) 1px, transparent 1px), linear-gradient(rgba(${GRID},0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(${GRID},0.035) 1px, transparent 1px)`,
    backgroundSize: "40px 40px, 40px 40px, 8px 8px, 8px 8px",
    backgroundPosition: "-1px -1px",
    boxShadow: active ? `inset 0 0 0 1.5px rgba(${GRID},0.7)` : `inset 0 0 0 1px rgba(${GRID},0.12)`,
    transition: "box-shadow 0.15s",
  }}>{children}</div>
);
